import React, { useMemo, useState } from "react";
import {
  ScreenshotGallery,
  formatMsDuration,
  formatToolArgs,
  formatUsage,
  showsEvidenceScreenshots,
} from "./eventsTimelineView";
import { ToolPayloadPanel } from "./ToolCallDetails";
import { formatUsd } from "./outcomeConfig";
import {
  buildConversationTree,
  conversationStats,
  runTotals,
} from "./conversationModel";
import { toolTheme, usedNamespaces } from "./conversationTheme";
import ContextMeter from "./ContextMeter";
import ContextManifest from "./ContextManifest";
import { ProseText } from "./TimelineMarkdown";

/**
 * Claude-Code-style transcript for an agent run.
 *
 * Layout rules, in the order they matter for reading a 400-turn trace:
 *
 *  1. One line per fact. A tool invocation is a single row — dot, name, the
 *     argument that identifies it, latency — with its result on a continuation
 *     row clamped to one line until opened.
 *  2. Hue carries the tool family (see conversationTheme): the eye finds the
 *     browser section or the issues section without reading.
 *  3. A turn is a visible block, not a hairline: numbered chip, its own card,
 *     model spend on the right. Turn boundaries are the main landmark when
 *     scrolling a long run.
 *  4. Prose is prose — sans-serif, roomy, near-black — so it never reads as
 *     one more mono line among the tool calls.
 *  5. Anything heavy (full args, full result, screenshots) is one click away.
 */

const RESULT_CLAMP = 150;

function firstLine(text, limit = RESULT_CLAMP) {
  if (!text) return "";
  const flat = String(text).replace(/\s+/g, " ").trim();
  return flat.length > limit ? `${flat.slice(0, limit)}…` : flat;
}

function byteLabel(text) {
  if (!text) return null;
  const n = String(text).length;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Duration with sub-second resolution.
 *
 * The shared formatMsDuration goes through formatDuration(seconds), which
 * renders a 49 ms tool call as "0s" — most memory/issues calls are that fast,
 * so a column of "0s" says nothing. Kept local rather than changing the shared
 * helper, which the Clean view still formats with.
 */
function formatLatency(ms) {
  if (ms == null) return null;
  const n = Number(ms);
  if (!Number.isFinite(n)) return null;
  if (n < 1000) return `${Math.round(n)}ms`;
  return formatMsDuration(n);
}

/** Slow calls earn emphasis: latency is the usual reason a run drags. */
function latencyClass(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n)) return "text-gray-300";
  if (n >= 60000) return "text-red-600 font-medium";
  if (n >= 10000) return "text-amber-600";
  return "text-gray-400";
}

function shortModel(model) {
  if (!model) return null;
  return String(model)
    .replace(/^claude-/, "")
    .replace(/-\d{8}$/, "");
}

function ToolName({ name, theme }) {
  const idx = name.indexOf(":");
  if (idx < 0) return <span className='text-gray-800 font-medium'>{name}</span>;
  return (
    <>
      <span className={theme.nsText}>{name.slice(0, idx)}</span>
      <span className='text-gray-300'>:</span>
      <span className='text-gray-900 font-medium'>{name.slice(idx + 1)}</span>
    </>
  );
}

function ResultRow({ item, theme }) {
  const [open, setOpen] = useState(false);
  const result = item.result;
  const screenshots = [
    ...(item.screenshotUrls || []),
    ...(result?.screenshotUrls || []),
  ];
  const hasScreenshots =
    screenshots.length > 0 && showsEvidenceScreenshots(item.name);

  if (item.blockedReason) {
    return (
      <div className='ml-[7px] pl-3 border-l-2 border-amber-300 py-0.5 text-[11px] text-amber-800'>
        blocked — {item.blockedReason}
      </div>
    );
  }

  if (!result && !hasScreenshots) {
    return (
      <div
        className={`ml-[7px] pl-3 border-l-2 ${theme.rail} py-0.5 text-[11px] text-gray-300 italic`}>
        no result recorded
      </div>
    );
  }

  const text = result?.text || "";
  const size = byteLabel(text);
  const expandable = text.length > RESULT_CLAMP || hasScreenshots;
  const isError = Boolean(result?.isError);

  return (
    <div
      className={`ml-[7px] pl-3 border-l-2 ${
        isError ? "border-red-300" : theme.rail
      }`}>
      <div className='flex items-baseline gap-2 py-0.5'>
        <button
          type='button'
          onClick={() => expandable && setOpen((v) => !v)}
          className={`flex-1 min-w-0 text-left font-mono text-[11px] ${
            isError ? "text-red-700" : "text-gray-600"
          } ${expandable ? "hover:text-gray-900 cursor-pointer" : "cursor-default"}`}>
          <span className='truncate block'>
            {text ? firstLine(text) : `${screenshots.length} screenshot(s)`}
          </span>
        </button>
        {hasScreenshots && (
          <span className='text-[10px] px-1 rounded bg-pink-50 text-pink-700 shrink-0'>
            {screenshots.length} img
          </span>
        )}
        {size && (
          <span className='text-[10px] text-gray-400 shrink-0 tabular-nums'>
            {size}
          </span>
        )}
        {expandable && (
          <span className='text-[10px] text-gray-400 shrink-0 select-none w-3'>
            {open ? "▾" : "▸"}
          </span>
        )}
      </div>
      {open && (
        <div className='my-2 p-2 rounded-lg bg-white border border-gray-200 space-y-2'>
          {text && <ToolPayloadPanel value={text} showHeader={false} />}
          {hasScreenshots && (
            <ScreenshotGallery urls={screenshots} caption={item.name} />
          )}
        </div>
      )}
    </div>
  );
}

function ToolRow({ item, events, env, traceRunId }) {
  const [argsOpen, setArgsOpen] = useState(false);
  const [childrenOpen, setChildrenOpen] = useState(false);
  const theme = toolTheme(item.name, item.isError);
  // A declared intent outranks the argument summary: for a `browsing:evaluate`
  // the summary IS the opaque thing the operator came here to understand — a
  // 1 kB blob of JS clipped at 120 chars. The args stay one click away below.
  const summary = item.intent || formatToolArgs(item.name, item.args);
  const duration = formatLatency(item.durationMs);
  const hasArgs = item.args != null && item.args !== "";

  return (
    <div
      className={`py-px ${
        item.isError ? "-mx-2 px-2 rounded bg-red-50 border-l-2 border-red-400" : ""
      }`}>
      <div className='flex items-baseline gap-2 text-xs font-mono'>
        <span className={`select-none shrink-0 ${theme.dot}`}>●</span>
        <button
          type='button'
          onClick={() => hasArgs && setArgsOpen((v) => !v)}
          className={`shrink-0 ${
            hasArgs ? "hover:bg-gray-100 rounded px-0.5 -mx-0.5" : "cursor-default"
          }`}
          title={hasArgs ? "show arguments" : undefined}>
          <ToolName name={item.name} theme={theme} />
        </button>
        {summary && (
          <span
            /* Solid gray-700 when the agent stated its intent, muted gray-500
               when we are only echoing arguments back. Different registers on
               purpose: one is a claim about why, the other is just data. */
            className={`truncate min-w-0 ${
              item.intent ? "text-gray-700" : "text-gray-500"
            }`}
            title={summary}>
            {summary}
          </span>
        )}
        <span className='flex-1' />
        {duration && (
          <span
            className={`text-[10px] shrink-0 tabular-nums ${latencyClass(
              item.durationMs
            )}`}>
            {duration}
          </span>
        )}
      </div>
      {argsOpen && (
        <div className='my-2 ml-[7px] pl-3 border-l-2 border-gray-200'>
          <div className='p-2 rounded-lg bg-white border border-gray-200'>
            <ToolPayloadPanel label='args' value={item.args} />
          </div>
        </div>
      )}
      <ResultRow item={item} theme={theme} />

      {/* Workers dispatched by this call, in place rather than at the bottom
          of the run. */}
      {item.children.length > 0 && (
        <div className='mt-1 ml-[7px] pl-3 border-l-2 border-amber-200'>
          <button
            type='button'
            onClick={() => setChildrenOpen((v) => !v)}
            className='flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 hover:text-amber-900 py-0.5'>
            <span className='select-none w-2'>{childrenOpen ? "▾" : "▸"}</span>
            {item.children.length} worker
            {item.children.length === 1 ? "" : "s"} dispatched
          </button>
          {childrenOpen &&
            item.children.map((child) => (
              <RunNode key={child.runId} node={child} depth={1} defaultOpen={false} events={events} env={env} traceRunId={traceRunId} />
            ))}
        </div>
      )}
    </div>
  );
}

function HookRow({ item }) {
  const tone = item.isError
    ? "bg-red-50 border-red-400 text-red-800"
    : item.isWarning
    ? "bg-amber-50 border-amber-400 text-amber-900"
    : "bg-gray-50 border-gray-300 text-gray-600";
  return (
    <div
      className={`flex items-baseline gap-2 my-1 py-1 px-2 rounded border-l-2 text-[11px] ${tone}`}>
      <span className='select-none shrink-0'>⚠</span>
      <span className='font-mono font-medium shrink-0'>{item.hook}</span>
      <span className='min-w-0'>{item.text}</span>
    </div>
  );
}

function UserReplyRow({ item }) {
  return (
    <div className='my-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200'>
      <div className='text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1'>
        operator reply
      </div>
      <ProseText
        text={item.text}
        className='text-xs text-emerald-900 whitespace-pre-wrap leading-relaxed'
      />
    </div>
  );
}

function OtherRow({ item }) {
  return (
    <div className='flex items-baseline gap-2 py-0.5 text-[11px] text-gray-400 font-mono'>
      <span className='select-none'>·</span>
      <span className='shrink-0'>{item.type}</span>
      <span className='truncate'>{firstLine(JSON.stringify(item.fields), 120)}</span>
    </div>
  );
}

function Turn({ turn, events, env, traceRunId }) {
  const model = shortModel(turn.llm?.model);
  const usage = formatUsage(turn.llm?.usage);
  const cacheRead = turn.llm?.usage?.cache_read_tokens;
  const meta = [
    model,
    formatLatency(turn.durationMs),
    turn.costUsd ? formatUsd(turn.costUsd) : null,
    usage,
    cacheRead ? `${cacheRead} cached` : null,
    turn.llmCalls > 1 ? `${turn.llmCalls} llm calls` : null,
  ].filter(Boolean);

  const errors = turn.items.filter((i) => i.isError).length;

  return (
    <section
      className={`mt-3 rounded-lg border overflow-hidden ${
        errors ? "border-red-200" : "border-gray-200"
      }`}>
      <header
        className={`flex items-center gap-2 px-3 py-1.5 border-b ${
          errors ? "bg-red-50 border-red-100" : "bg-gray-50 border-gray-100"
        }`}>
        <span
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded tabular-nums ${
            errors ? "bg-red-600 text-white" : "bg-gray-700 text-white"
          }`}>
          TURN {turn.index}
        </span>
        {errors > 0 && (
          <span className='text-[10px] font-medium text-red-700'>
            {errors} failed call{errors === 1 ? "" : "s"}
          </span>
        )}
        <span className='flex-1' />
        {meta.length > 0 && (
          <span className='text-[10px] text-gray-500 font-mono truncate'>
            {meta.join(" · ")}
          </span>
        )}
        {turn.llm?.usage && (
          <ContextMeter
            portal
            title={`Turn ${turn.index} context`}
            llmMeta={turn.llm}
            usage={turn.llm.usage}
            events={events}
            eventIndex={turn.llmEventIndex ?? 0}
          />
        )}
      </header>

      <div className='px-3 py-2 bg-white'>
        {turn.texts.map((t, i) => (
          <div key={i} className='mb-2'>
            <ProseText
              text={t}
              className='text-[13px] leading-[1.65] text-gray-800 whitespace-pre-wrap'
            />
          </div>
        ))}
        {turn.items.map((item, i) => {
          if (item.kind === "tool")
            return <ToolRow key={i} item={item} events={events} env={env} traceRunId={traceRunId} />;
          if (item.kind === "hook") return <HookRow key={i} item={item} />;
          if (item.kind === "userReply") return <UserReplyRow key={i} item={item} />;
          return <OtherRow key={i} item={item} />;
        })}
      </div>
    </section>
  );
}

function RunNode({ node, depth, defaultOpen, events, env, traceRunId }) {
  const [open, setOpen] = useState(defaultOpen);
  const {
    tools: toolCount,
    errors: errorCount,
    warnings: warnCount,
    costUsd: cost,
  } = runTotals(node);
  const isRoot = depth === 0;
  const isWorker = (node.agentId || "").includes("worker");

  // Where the run ended up in its context window — the last turn that made an
  // llm call is the run's final context.
  const finalTurn = [...node.turns].reverse().find((t) => t.llm?.usage) || null;

  return (
    <div
      className={
        isRoot
          ? ""
          : `mt-1.5 pl-3 border-l-2 ${
              errorCount ? "border-red-200" : "border-indigo-200"
            }`
      }>
      {/* A row, not a <button>: ContextMeter is itself interactive and cannot
          be nested inside one. */}
      <div
        className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-lg transition-colors ${
          isRoot ? "bg-indigo-50 hover:bg-indigo-100" : "hover:bg-gray-100"
        }`}>
        <button
          type='button'
          onClick={() => setOpen((v) => !v)}
          className='flex items-center gap-2 min-w-0 flex-1 text-left'>
          <span className='text-gray-400 text-[10px] select-none w-3 shrink-0'>
            {open ? "▾" : "▸"}
          </span>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0 ${
              isWorker ? "bg-gray-200 text-gray-700" : "bg-indigo-600 text-white"
            }`}>
            {node.agentId || "agent"}
          </span>
          <span className='text-[10px] font-mono text-gray-400 truncate'>
            {node.runId}
          </span>
        </button>
        <span className='text-[10px] text-gray-500 font-mono shrink-0 tabular-nums'>
          {node.turns.length}t · {toolCount} tools
          {cost ? ` · ${formatUsd(cost)}` : ""}
        </span>
        {errorCount > 0 && (
          <span className='text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-semibold shrink-0'>
            {errorCount} err
          </span>
        )}
        {warnCount > 0 && (
          <span className='text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold shrink-0'>
            {warnCount} warn
          </span>
        )}
        {node.children.length > 0 && (
          <span className='text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0'>
            {node.children.length} child
          </span>
        )}
        {finalTurn && (
          <span className='shrink-0'>
            <ContextMeter
              portal
              title='Final context'
              llmMeta={finalTurn.llm}
              usage={finalTurn.llm.usage}
              events={events}
              eventIndex={finalTurn.llmEventIndex ?? 0}
            />
          </span>
        )}
      </div>

      {open && (
        <div className={isRoot ? "" : "pt-0.5"}>
          {/* What the agent was given, beside what it was asked. Both stay
              collapsed so the turn list is still the first thing you read.

              runId is the TRACE's run, not this node's: segment bodies are
              fetched via the run's DynamoDB row, and only the top-level run
              has one. Passing a worker or subagent id resolves to no website
              and the body lookup comes back empty. */}
          <ContextManifest
            manifest={node.contextManifest}
            env={env}
            runId={traceRunId || node.runId}
          />
          {node.prompt && (
            <details className='mt-2 rounded-lg bg-gray-50 border border-gray-200'>
              <summary className='cursor-pointer px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold'>
                prompt
              </summary>
              <div className='px-3 pb-2 pt-2 max-h-[240px] overflow-y-auto border-t border-gray-200'>
                <ProseText
                  text={node.prompt}
                  className='text-[12px] text-gray-700 whitespace-pre-wrap leading-relaxed'
                />
              </div>
            </details>
          )}
          {node.turns.map((t) => (
            <Turn key={t.seq} turn={t} events={events} env={env} traceRunId={traceRunId} />
          ))}
          {/* Children normally render at the call that spawned them; only the
              ones we could not place chronologically land here. */}
          {(node.trailingChildren || []).length > 0 && (
            <div className='mt-3'>
              <div className='text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1'>
                {node.trailingChildren.length} unplaced child run
                {node.trailingChildren.length === 1 ? "" : "s"}
              </div>
              {node.trailingChildren.map((child) => (
                <RunNode
                  key={child.runId}
                  node={child}
                  depth={depth + 1}
                  defaultOpen={false}
                  events={events}
                  env={env}
                  traceRunId={traceRunId}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Legend({ nodes }) {
  const namespaces = useMemo(() => usedNamespaces(nodes), [nodes]);
  if (!namespaces.length) return null;
  return (
    <div className='flex items-center gap-1.5 flex-wrap'>
      {namespaces.map((n) => (
        <span
          key={n.ns}
          className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${n.chipBg} ${n.chipText}`}>
          <span className={n.dot}>●</span>
          {n.ns}
        </span>
      ))}
    </div>
  );
}

export default function ConversationView({ events, rootRunId, env, traceRunId }) {
  const nodes = useMemo(
    () => buildConversationTree(events, rootRunId),
    [events, rootRunId]
  );
  const stats = useMemo(() => conversationStats(nodes), [nodes]);

  if (!nodes.length) {
    return (
      <div className='px-4 py-6 text-center text-xs text-gray-400'>
        No events in this window.
      </div>
    );
  }

  return (
    <div className='px-4 py-3 bg-gray-50/60'>
      <div className='flex items-center gap-3 flex-wrap pb-2 mb-2 border-b border-gray-200'>
        <span className='text-[11px] font-mono text-gray-600 tabular-nums'>
          {stats.runs} run{stats.runs === 1 ? "" : "s"} · {stats.turns} turns ·{" "}
          {stats.tools} tools
          {stats.costUsd ? ` · ${formatUsd(stats.costUsd)}` : ""}
        </span>
        {stats.errors > 0 && (
          <span className='text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-semibold'>
            {stats.errors} failed calls
          </span>
        )}
        {stats.warnings > 0 && (
          <span className='text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold'>
            {stats.warnings} warnings
          </span>
        )}
        <span className='flex-1' />
        <Legend nodes={nodes} />
        <span className='text-[10px] font-mono text-gray-400'>
          {(events || []).length} raw events
        </span>
      </div>
      {nodes.map((node, i) => (
        <RunNode
          key={node.runId}
          node={node}
          depth={0}
          defaultOpen={i === 0}
          events={events}
          env={env}
          traceRunId={traceRunId}
        />
      ))}
    </div>
  );
}
